import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

function AdvertisingBanner() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);

  // ✅ DETECTAR IMÁGENES AUTOMÁTICAMENTE
  useEffect(() => {
    const loadBanners = async () => {
      const detectedBanners = [];
      const maxAttempts = 30;
      let consecutiveFails = 0;
      
      for (let i = 1; i <= maxAttempts; i++) {
        const extensions = ['jpg', 'jpeg', 'png', 'webp'];
        let imageFound = false;
        
        for (const ext of extensions) {
          const imagePath = `/images/destinos/banners/${i}.${ext}`;
          
          try {
            await new Promise((resolve, reject) => {
              const img = new Image();
              img.onload = () => {
                detectedBanners.push({
                  id: i,
                  image: imagePath,
                  alt: `Promoción ${i}`,
                  link: null
                });
                imageFound = true;
                consecutiveFails = 0;
                resolve();
              };
              img.onerror = () => reject();
              img.src = imagePath;
            });
            break;
          } catch (error) {
            continue;
          }
        }
        
        if (!imageFound) {
          consecutiveFails++;
          if (consecutiveFails >= 3) break;
        }
      }
      
      console.log(`📸 ${detectedBanners.length} banners detectados`);
      setBanners(detectedBanners);
      setLoading(false);
    };

    loadBanners();
  }, []);

  // Auto-play cada 5 segundos
  useEffect(() => {
    if (!isAutoPlaying || banners.length === 0) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % banners.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, banners.length]);

  const goToPrevious = () => {
    setCurrentSlide((prev) => (prev - 1 + banners.length) % banners.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const goToNext = () => {
    setCurrentSlide((prev) => (prev + 1) % banners.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  if (loading) {
    return (
      <div className="relative w-full bg-gray-100 overflow-hidden rounded-xl shadow-lg my-4 md:my-8">
        <div className="flex items-center justify-center h-48 md:h-80 lg:h-[28rem]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ff681f]"></div>
        </div>
      </div>
    );
  }

  if (banners.length === 0) {
    return null; // No mostrar nada si no hay banners
  }

  return (
    <div className="relative w-full bg-gray-100 overflow-hidden rounded-xl shadow-lg my-4 md:my-8">
      {/* ✅ ALTURA RESPONSIVE: más bajo en mobile */}
      <div className="relative h-48 sm:h-64 md:h-80 lg:h-[28rem]">
        {/* Slides */}
        <div className="relative w-full h-full">
          {banners.map((banner, index) => (
            <div
              key={banner.id}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
              }`}
            >
              <img
                src={banner.image}
                alt={banner.alt}
                className="w-full h-full object-contain md:object-cover bg-gradient-to-br from-gray-100 to-gray-200"
                loading="lazy"
              />
            </div>
          ))}
        </div>

        {/* ✅ Botones de navegación - MÁS PEQUEÑOS EN MOBILE */}
        {banners.length > 1 && (
          <>
            <button
              onClick={goToPrevious}
              className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 p-2 md:p-3 rounded-full shadow-lg transition-all z-20"
              aria-label="Banner anterior"
            >
              <ChevronLeft size={20} className="md:w-6 md:h-6" />
            </button>

            <button
              onClick={goToNext}
              className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 p-2 md:p-3 rounded-full shadow-lg transition-all z-20"
              aria-label="Siguiente banner"
            >
              <ChevronRight size={20} className="md:w-6 md:h-6" />
            </button>
          </>
        )}

        {/* ✅ Degradado inferior */}
        <div className="absolute bottom-0 left-0 right-0 h-20 md:h-32 bg-gradient-to-t from-black/50 via-black/20 to-transparent z-10" />
        
        {/* ✅ Contador - MÁS PEQUEÑO Y MEJOR POSICIONADO EN MOBILE */}
        <div className="absolute top-3 right-3 md:top-6 md:right-6 z-20 bg-black/60 backdrop-blur-sm text-white px-2.5 py-1.5 md:px-4 md:py-2 rounded-full text-xs md:text-sm font-semibold shadow-lg">
          {currentSlide + 1} / {banners.length}
        </div>

        {/* ✅ Indicadores de puntos en la parte inferior (solo mobile) */}
        {banners.length > 1 && banners.length <= 10 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-20 md:hidden">
            {banners.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setCurrentSlide(index);
                  setIsAutoPlaying(false);
                  setTimeout(() => setIsAutoPlaying(true), 10000);
                }}
                className={`transition-all ${
                  index === currentSlide
                    ? 'w-6 h-2 bg-white'
                    : 'w-2 h-2 bg-white/50 hover:bg-white/75'
                } rounded-full`}
                aria-label={`Ir a slide ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* ✅ Footer - MÁS COMPACTO EN MOBILE */}
      <div className="bg-white px-3 py-2 md:px-6 md:py-3 text-center">
        <p className="text-xs md:text-sm text-gray-600 font-medium">
          📢 Promociones exclusivas • Viajes grupales
        </p>
      </div>
    </div>
  );
}

export default AdvertisingBanner;